import { useCallback, useEffect, useRef, useState } from 'react';
import { canvasToNormalizedSignatureBlob } from './signatureImageExport';

export type SignaturePadMode = 'draw' | 'erase';

export interface UseSignaturePadOptions {
  lineWidthDraw?: number;
  lineWidthErase?: number;
}

function pointerToCanvas(
  canvas: HTMLCanvasElement,
  event: PointerEvent
): { x: number; y: number } | null {
  const dpr = window.devicePixelRatio || 1;
  const logicalW = canvas.width / dpr;
  const logicalH = canvas.height / dpr;
  const cssW = canvas.clientWidth;
  const cssH = canvas.clientHeight;
  if (logicalW <= 0 || logicalH <= 0 || cssW <= 0 || cssH <= 0) return null;

  /* offsetX/Y are in the canvas local axes (correct when ancestors use rotate/transform). */
  if (event.target === canvas && typeof event.offsetX === 'number' && typeof event.offsetY === 'number') {
    return {
      x: (event.offsetX / cssW) * logicalW,
      y: (event.offsetY / cssH) * logicalH,
    };
  }

  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    x: ((event.clientX - rect.left) / rect.width) * logicalW,
    y: ((event.clientY - rect.top) / rect.height) * logicalH,
  };
}

export function useSignaturePad(options: UseSignaturePadOptions = {}) {
  const lineWidthDraw = options.lineWidthDraw ?? 2.5;
  const lineWidthErase = options.lineWidthErase ?? 18;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [mode, setMode] = useState<SignaturePadMode>('draw');
  const [hasStrokes, setHasStrokes] = useState(false);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    /* clientWidth/Height ignore ancestor rotation; getBoundingClientRect does not. */
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.floor(cssW * dpr));
    const h = Math.max(1, Math.floor(cssH * dpr));
    if (canvas.width === w && canvas.height === h) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const imageData =
      canvas.width > 0 && canvas.height > 0 ? ctx.getImageData(0, 0, canvas.width, canvas.height) : null;

    canvas.width = w;
    canvas.height = h;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (imageData && cssW > 0 && cssH > 0) {
      const temp = document.createElement('canvas');
      temp.width = imageData.width;
      temp.height = imageData.height;
      temp.getContext('2d')?.putImageData(imageData, 0, 0);
      ctx.drawImage(temp, 0, 0, cssW, cssH);
    }
  }, []);

  useEffect(() => {
    resizeCanvas();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onResize = () => resizeCanvas();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    ro?.observe(canvas);
    const pad = canvas.parentElement;
    if (pad && ro) ro.observe(pad);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      ro?.disconnect();
    };
  }, [resizeCanvas]);

  const getPoint = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return pointerToCanvas(canvas, e.nativeEvent);
  }, []);

  const applyStrokeStyle = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (mode === 'erase') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
        ctx.lineWidth = lineWidthErase;
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = lineWidthDraw;
      }
    },
    [mode, lineWidthDraw, lineWidthErase]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const pt = getPoint(e);
      if (!canvas || !ctx || !pt) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      lastPointRef.current = pt;
      applyStrokeStyle(ctx);
      ctx.beginPath();
      ctx.moveTo(pt.x, pt.y);
    },
    [applyStrokeStyle, getPoint]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const pt = getPoint(e);
      const last = lastPointRef.current;
      if (!ctx || !pt || !last) return;
      e.preventDefault();
      applyStrokeStyle(ctx);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      lastPointRef.current = pt;
      setHasStrokes(true);
    },
    [applyStrokeStyle, getPoint]
  );

  const endStroke = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (drawingRef.current) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    drawingRef.current = false;
    lastPointRef.current = null;
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = 'source-over';
    setHasStrokes(false);
  }, []);

  const exportPngBlob = useCallback((): Promise<Blob | null> => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStrokes) {
      return Promise.resolve(null);
    }
    return canvasToNormalizedSignatureBlob(canvas);
  }, [hasStrokes]);

  return {
    canvasRef,
    mode,
    setMode,
    hasStrokes,
    clear,
    exportPngBlob,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp: endStroke,
    handlePointerCancel: endStroke,
  };
}
